// src/pages/admin/ManageElectionDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL_ELECTIONS } from '../../../utils/constants';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import * as XLSX from 'xlsx';
import ElectionProtectedRoute from '../../../components/auth/ElectionProtectedRoute';

const ManageElectionDetailsPage = () => {
  const { electionId } = useParams();
  const navigate = useNavigate();
  const [election, setElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('positions'); // positions, students
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);

  // Position States
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [positionForm, setPositionForm] = useState({
    title: '',
    description: '',
    order: 0
  });

  // Candidate States
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [candidateForm, setCandidateForm] = useState({
    fullName: '',
    bio: '',
    manifesto: '',
    photo: '',
    order: 0
  });

  // Student States
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [students, setStudents] = useState([]);
  const [studentForm, setStudentForm] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    indexNumber: '',
    uniqueCode: ''
  });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet);

        // Validate and map data
        const mappedData = data.map((row, index) => ({
          fullName: row['Full Name'] || row['fullName'] || '',
          email: row['Email'] || row['email'] || '',
          phoneNumber: row['Phone Number'] || row['phoneNumber'] || row['Phone'] || '',
          indexNumber: row['Index Number'] || row['indexNumber'] || row['Index'] || '',
          uniqueCode: row['Unique Code'] || row['uniqueCode'] || row['Code'] || '',
          _rowNumber: index + 2 // Excel row number (accounting for header)
        }));

        // Filter out empty rows
        const validData = mappedData.filter(row => row.fullName.trim() !== '');

        setPreviewData(validData);
      } catch (error) {
        console.error('Error reading file:', error);
        alert('Error reading Excel file. Please check the format.');
      }
    };

    reader.readAsBinaryString(file);
  };

  const handleBulkUploadFromExcel = async () => {
    if (!election || previewData.length === 0) return;

    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/students/bulk-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          schoolId: election.schoolId,
          students: previewData.map(({ _rowNumber, ...student }) => student)
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(`Successfully uploaded ${data.data.count} students`);
        setShowBulkUploadModal(false);
        setUploadFile(null);
        setPreviewData([]);
        fetchStudents();
      } else {
        alert(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading students:', error);
      alert('Failed to upload students');
    }
  };

  const downloadExcelTemplate = () => {
    const template = [
      {
        'Full Name': 'John Doe',
        'Email': 'john@school.edu',
        'Phone Number': '0241234567',
        'Index Number': 'UG00001',
        'Unique Code': ''
      },
      {
        'Full Name': 'Jane Smith',
        'Email': 'jane@school.edu',
        'Phone Number': '0241234568',
        'Index Number': 'UG00002',
        'Unique Code': ''
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');
    
    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, // Full Name
      { wch: 25 }, // Email
      { wch: 15 }, // Phone Number
      { wch: 15 }, // Index Number
      { wch: 15 }  // Unique Code
    ];

    XLSX.writeFile(wb, 'student_upload_template.xlsx');
  };

  useEffect(() => {
    fetchElection();
    if (activeTab === 'students') {
      fetchStudents();
    }
  }, [electionId, activeTab]);

  const fetchElection = async () => {
    try {
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/elections/${electionId}`);
      const data = await response.json();
      if (data.success) {
        setElection(data.data);
      }
    } catch (error) {
      console.error('Error fetching election:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    if (!election) return;
    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/students/school/${election.schoolId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setStudents(data.data.students || []);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  // Position Functions
  const handleCreatePosition = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/positions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...positionForm, electionId })
      });

      const data = await response.json();
      if (data.success) {
        setShowPositionModal(false);
        setPositionForm({ title: '', description: '', order: 0 });
        fetchElection();
      }
    } catch (error) {
      console.error('Error creating position:', error);
    }
  };

  const handleDeletePosition = async (positionId) => {
    if (!ConfirmDialog('Delete this position and all its candidates?')) return;

    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/positions/${positionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        fetchElection();
      }
    } catch (error) {
      console.error('Error deleting position:', error);
    }
  };

  // Candidate Functions
  const handleCreateCandidate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/candidates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...candidateForm, positionId: selectedPosition.id })
      });

      const data = await response.json();
      if (data.success) {
        setShowCandidateModal(false);
        setCandidateForm({ fullName: '', bio: '', manifesto: '', photo: '', order: 0 });
        fetchElection();
      }
    } catch (error) {
      console.error('Error creating candidate:', error);
    }
  };

  const handleDeleteCandidate = async (candidateId) => {
    if (!ConfirmDialog('Delete this candidate?')) return;

    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/candidates/${candidateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        fetchElection();
      }
    } catch (error) {
      console.error('Error deleting candidate:', error);
    }
  };

  // Student Functions
  const handleBulkUploadStudents = async (e) => {
    e.preventDefault();
    if (!election) return;

    try {
      const token = localStorage.getItem('election_token');
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/students/bulk-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          schoolId: election.schoolId,
          students: [studentForm]
        })
      });

      const data = await response.json();
      if (data.success) {
        setShowStudentModal(false);
        setStudentForm({ fullName: '', email: '', phoneNumber: '', indexNumber: '', uniqueCode: '' });
        fetchStudents();
      }
    } catch (error) {
      console.error('Error adding student:', error);
    }
  };

  if (loading) {
    return (
      <ElectionProtectedRoute>
        <div className="flex items-center justify-center min-h-screen bg-black">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-400 font-semibold">Loading Election...</p>
          </div>
        </div>
      </ElectionProtectedRoute>
    );
  }

  return (
    <ElectionProtectedRoute>
      <div className="min-h-screen bg-black">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 border-b border-red-900/30 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <button
                  onClick={() => navigate('/admin/manage-elections')}
                  className="flex items-center gap-2 text-red-400 hover:text-red-300 mb-3 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Elections
                </button>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <span className="text-red-500">⚙️</span>
                  {election?.title}
                </h1>
                <p className="text-gray-400 mt-1">{election?.school?.name}</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 border-b border-gray-800">
              <button
                onClick={() => setActiveTab('positions')}
                className={`px-6 py-3 font-semibold transition-all ${
                  activeTab === 'positions'
                    ? 'text-red-500 border-b-2 border-red-500'
                    : 'text-gray-400 hover:text-red-400'
                }`}
              >
                Positions & Candidates
              </button>
              <button
                onClick={() => setActiveTab('students')}
                className={`px-6 py-3 font-semibold transition-all ${
                  activeTab === 'students'
                    ? 'text-red-500 border-b-2 border-red-500'
                    : 'text-gray-400 hover:text-red-400'
                }`}
              >
                Students
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeTab === 'positions' && (
            <>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Positions</h2>
                <button
                  onClick={() => setShowPositionModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Position
                </button>
              </div>

              {election?.positions?.length === 0 ? (
                <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl shadow-xl p-16">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-red-900/20 rounded-full mb-4 border border-red-500/30">
                      <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">No positions added yet</h3>
                    <p className="text-gray-400">Start by adding positions for this election</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {election?.positions?.map((position) => (
                    <div key={position.id} className="group relative">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-xl opacity-0 group-hover:opacity-20 blur transition duration-300"></div>
                      <div className="relative bg-gradient-to-br from-gray-900 to-black border border-gray-800 group-hover:border-red-500/50 rounded-xl shadow-xl p-6">
                        <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-800">
                          <div>
                            <h3 className="text-xl font-bold text-white">{position.title}</h3>
                            <p className="text-gray-400 text-sm mt-1">{position.description}</p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedPosition(position);
                                setShowCandidateModal(true);
                              }}
                              className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg font-semibold text-sm shadow-lg shadow-green-600/20 transition-all duration-300"
                            >
                              + Add Candidate
                            </button>
                            <button
                              onClick={() => handleDeletePosition(position.id)}
                              className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold text-sm shadow-lg shadow-red-600/20 transition-all duration-300"
                            >
                              Delete
                            </button>
                          </div>
                        </div>

                        {/* Candidates */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {position.candidates?.map((candidate) => (
                            <div key={candidate.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 hover:border-red-500/50 transition-all">
                              {candidate.photo && (
                                <img
                                  src={candidate.photo}
                                  alt={candidate.fullName}
                                  className="w-20 h-20 rounded-full mx-auto mb-3 object-cover border-2 border-red-500/30"
                                />
                              )}
                              <h4 className="font-bold text-center text-white mb-2">{candidate.fullName}</h4>
                              {candidate.bio && <p className="text-sm text-gray-400 mb-3">{candidate.bio}</p>}
                              <button
                                onClick={() => handleDeleteCandidate(candidate.id)}
                                className="w-full px-3 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-500/30 rounded-lg text-sm font-semibold transition-all"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          {position.candidates?.length === 0 && (
                            <div className="col-span-3 text-center py-8 text-gray-500 text-sm">
                              No candidates added yet
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'students' && (
            <>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Students ({students.length})</h2>
                <div className="flex gap-3">
                  <button
                    onClick={downloadExcelTemplate}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg font-semibold shadow-lg shadow-green-600/30 flex items-center gap-2 transition-all duration-300 transform hover:scale-105"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download Template
                  </button>
                  <button
                    onClick={() => setShowBulkUploadModal(true)}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-lg font-semibold shadow-lg shadow-purple-600/30 flex items-center gap-2 transition-all duration-300 transform hover:scale-105"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Bulk Upload Excel
                  </button>
                  <button
                    onClick={() => setShowStudentModal(true)}
                    className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105"
                  >
                    + Add Student
                  </button>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl shadow-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-800/50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">#</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">Name</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">Email</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">Phone</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">Index</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {students.map((student, index) => (
                        <tr key={student.id} className="hover:bg-gray-800/30 transition-colors">
                          <td className="px-6 py-4 text-sm text-gray-500">{index + 1}</td>
                          <td className="px-6 py-4 text-sm font-medium text-white">{student.fullName}</td>
                          <td className="px-6 py-4 text-sm text-gray-400">{student.email || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-gray-400">{student.phoneNumber || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-gray-400">{student.indexNumber || 'N/A'}</td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                              student.hasVoted 
                                ? 'bg-green-900/30 text-green-400 border-green-500/50' 
                                : 'bg-gray-800 text-gray-400 border-gray-600'
                            }`}>
                              {student.hasVoted ? '✓ Voted' : 'Not Voted'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {students.length === 0 && (
                    <div className="text-center py-16">
                      <div className="inline-flex items-center justify-center w-20 h-20 bg-red-900/20 rounded-full mb-4 border border-red-500/30">
                        <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">No students added yet</h3>
                      <p className="text-gray-400">Add students individually or bulk upload from Excel</p>
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {students.length > 0 && (
                  <div className="bg-gray-800/50 px-6 py-4 flex items-center justify-between border-t border-gray-800">
                    <div className="text-sm text-gray-400">
                      Showing <span className="font-semibold text-white">{students.length}</span> students
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bulk Upload Excel Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-2xl max-w-4xl w-full p-8 max-h-[90vh] overflow-y-auto shadow-2xl relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl opacity-20 blur"></div>
            
            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                  <span className="text-red-500">📊</span>
                  Bulk Upload Students from Excel
                </h2>
                <button
                  onClick={() => {
                    setShowBulkUploadModal(false);
                    setUploadFile(null);
                    setPreviewData([]);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Instructions */}
              <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                  <span>📋</span>
                  Instructions:
                </h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-300">
                  <li>Download the Excel template below</li>
                  <li>Fill in student details (Full Name is required)</li>
                  <li>Save the file and upload it here</li>
                  <li>Preview and confirm before uploading</li>
                </ol>
              </div>

              {/* Template Download */}
              <div className="mb-6">
                <button
                  onClick={downloadExcelTemplate}
                  className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg font-semibold flex items-center justify-center gap-2 shadow-lg shadow-green-600/30 transition-all duration-300 transform hover:scale-105"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Excel Template
                </button>
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Upload Excel File (.xlsx, .xls)
                </label>
                <div className="border-2 border-dashed border-gray-700 hover:border-red-500/50 rounded-lg p-8 text-center transition-colors">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="excel-upload"
                  />
                  <label htmlFor="excel-upload" className="cursor-pointer">
                    <svg className="mx-auto h-12 w-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-300">
                      {uploadFile ? uploadFile.name : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Excel files only</p>
                  </label>
                </div>
              </div>

              {/* Preview */}
              {previewData.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-white mb-3">
                    Preview ({previewData.length} students)
                  </h3>
                  <div className="border border-gray-800 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-800 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300">#</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300">Full Name</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300">Email</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300">Phone</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-300">Index</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800">
                        {previewData.slice(0, 10).map((student, index) => (
                          <tr key={index} className="hover:bg-gray-800/30">
                            <td className="px-4 py-2 text-gray-500">{index + 1}</td>
                            <td className="px-4 py-2 font-medium text-white">{student.fullName}</td>
                            <td className="px-4 py-2 text-gray-400">{student.email || '-'}</td>
                            <td className="px-4 py-2 text-gray-400">{student.phoneNumber || '-'}</td>
                            <td className="px-4 py-2 text-gray-400">{student.indexNumber || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {previewData.length > 10 && (
                      <div className="bg-gray-800 px-4 py-2 text-sm text-gray-400 text-center">
                        ... and {previewData.length - 10} more students
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-800">
                <button
                  onClick={() => {
                    setShowBulkUploadModal(false);
                    setUploadFile(null);
                    setPreviewData([]);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:border-gray-600 text-white rounded-lg font-semibold transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUploadFromExcel}
                  disabled={previewData.length === 0}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  Upload {previewData.length} Student{previewData.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Position Modal */}
      {showPositionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-2xl max-w-md w-full p-8 shadow-2xl relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl opacity-20 blur"></div>
            
            <div className="relative">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="text-red-500">➕</span>
                Add Position
              </h2>
              <form onSubmit={handleCreatePosition} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={positionForm.title}
                    onChange={(e) => setPositionForm({ ...positionForm, title: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="President"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Description</label>
                  <textarea
                    value={positionForm.description}
                    onChange={(e) => setPositionForm({ ...positionForm, description: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    rows="3"
                    placeholder="Position description..."
                  />
                </div>
                <div className="flex gap-3 pt-4 border-t border-gray-800">
                  <button
                    type="button"
                    onClick={() => setShowPositionModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:border-gray-600 text-white rounded-lg font-semibold transition-all duration-300"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Modal */}
      {showCandidateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-2xl max-w-md w-full p-8 max-h-[90vh] overflow-y-auto shadow-2xl relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl opacity-20 blur"></div>
            
            <div className="relative">
              <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                <span className="text-red-500">👤</span>
                Add Candidate
              </h2>
              <p className="text-gray-400 mb-6">for {selectedPosition?.title}</p>
              
              <form onSubmit={handleCreateCandidate} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={candidateForm.fullName}
                    onChange={(e) => setCandidateForm({ ...candidateForm, fullName: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Bio</label>
                  <textarea
                    value={candidateForm.bio}
                    onChange={(e) => setCandidateForm({ ...candidateForm, bio: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    rows="2"
                    placeholder="Brief biography..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Manifesto</label>
                  <textarea
                    value={candidateForm.manifesto}
                    onChange={(e) => setCandidateForm({ ...candidateForm, manifesto: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    rows="3"
                    placeholder="Campaign manifesto..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Photo URL</label>
                  <input
                    type="url"
                    value={candidateForm.photo}
                    onChange={(e) => setCandidateForm({ ...candidateForm, photo: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="https://example.com/photo.jpg"
                  />
                </div>
                <div className="flex gap-3 pt-4 border-t border-gray-800">
                  <button
                    type="button"
                    onClick={() => setShowCandidateModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:border-gray-600 text-white rounded-lg font-semibold transition-all duration-300"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105"
                  >
                    Add Candidate
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Student Modal */}
      {showStudentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-2xl max-w-md w-full p-8 shadow-2xl relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl opacity-20 blur"></div>
            
            <div className="relative">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="text-red-500">👨‍🎓</span>
                Add Student
              </h2>
              <form onSubmit={handleBulkUploadStudents} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={studentForm.fullName}
                    onChange={(e) => setStudentForm({ ...studentForm, fullName: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={studentForm.email}
                    onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="john@school.edu"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={studentForm.phoneNumber}
                    onChange={(e) => setStudentForm({ ...studentForm, phoneNumber: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="0241234567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Index Number</label>
                  <input
                    type="text"
                    value={studentForm.indexNumber}
                    onChange={(e) => setStudentForm({ ...studentForm, indexNumber: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="UG00001"
                  />
                </div>
                <div className="flex gap-3 pt-4 border-t border-gray-800">
                  <button
                    type="button"
                    onClick={() => setShowStudentModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 hover:bg-gray-700 hover:border-gray-600 text-white rounded-lg font-semibold transition-all duration-300"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-semibold shadow-lg shadow-red-600/30 transition-all duration-300 transform hover:scale-105"
                  >
                    Add Student
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </ElectionProtectedRoute>
  );
};

export default ManageElectionDetailsPage;